import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";

import { ApiError } from "../api/client";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import { Button, cx } from "./ui";
import { ACTIVITY_KINDS } from "./ActivityForm";
import { formatDateTime, formatElapsed } from "../lib/format";
import type { Activity, ActivityWrite } from "../api/types";

/**
 * The inline "log a call" panel from the queue row.
 *
 * Compact on purpose: it appears inside a table row, so it is subject and detail
 * only. Anything more belongs in the lead's modal. Fields change with the kind
 * chosen, because a call has a direction and a task has a due date and a note
 * has neither.
 */
export function QuickAddForm({
  onSave,
  onCancel,
}: {
  onSave: (body: ActivityWrite) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<ActivityWrite["type"]>("note");
  const [subject, setSubject] = useState("");
  const [detail, setDetail] = useState("");
  const [direction, setDirection] = useState("outbound");
  const [outcome, setOutcome] = useState("");
  const [dueAt, setDueAt] = useState("");

  const save = useMutation({
    mutationFn: () =>
      onSave({
        type: kind,
        subject: subject || undefined,
        body: detail || undefined,
        direction: kind === "call" ? direction : null,
        outcome: kind === "call" && outcome ? outcome : null,
        due_at: kind === "task" && dueAt ? new Date(dueAt).toISOString() : null,
      }),
    onSuccess: () => {
      setSubject("");
      setDetail("");
      setOutcome("");
      setDueAt("");
      onCancel();
    },
  });

  const error = save.error instanceof ApiError ? save.error : null;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {ACTIVITY_KINDS.map((k) => (
          <button
            key={k.value}
            type="button"
            onClick={() => setKind(k.value)}
            aria-pressed={kind === k.value}
            title={k.label}
            className={cx(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition",
              kind === k.value
                ? "bg-slate-900 text-white"
                : "bg-white text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50",
            )}
          >
            <k.icon size={15} />
            {k.label}
          </button>
        ))}
      </div>

      <input
        value={subject}
        onChange={(event) => setSubject(event.target.value)}
        placeholder={kind === "task" ? "What needs doing" : "One line about it"}
        aria-label="Subject"
        className="w-full rounded-md border-0 bg-white px-2.5 py-1.5 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
      />

      {kind === "call" ? (
        <div className="flex gap-1.5">
          <select
            value={direction}
            onChange={(event) => setDirection(event.target.value)}
            aria-label="Direction"
            className="flex-1 rounded-md border-0 bg-white px-2 py-1 text-xs ring-1 ring-inset ring-slate-300"
          >
            <option value="outbound">Outbound</option>
            <option value="inbound">Inbound</option>
          </select>
          <select
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
            aria-label="Outcome"
            className="flex-1 rounded-md border-0 bg-white px-2 py-1 text-xs ring-1 ring-inset ring-slate-300"
          >
            <option value="">No outcome</option>
            <option value="connected">Connected</option>
            <option value="voicemail">Voicemail</option>
            <option value="no_answer">No answer</option>
            <option value="busy">Busy</option>
          </select>
        </div>
      ) : null}

      {kind === "task" ? (
        <input
          type="date"
          value={dueAt}
          onChange={(event) => setDueAt(event.target.value)}
          aria-label="Due"
          className="w-full rounded-md border-0 bg-white px-2.5 py-1.5 text-xs ring-1 ring-inset ring-slate-300"
        />
      ) : null}

      <textarea
        value={detail}
        onChange={(event) => setDetail(event.target.value)}
        rows={2}
        placeholder="Notes, outcomes or next steps"
        aria-label="Detail"
        className="w-full rounded-md border-0 bg-white px-2.5 py-1.5 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
      />

      {error ? (
        <p role="alert" className="text-xs text-rose-600">
          {error.message}
        </p>
      ) : null}

      <div className="flex items-center gap-1.5">
        <Button type="submit" variant="primary" disabled={save.isPending} className="px-2.5 py-1 text-xs">
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        <Button onClick={onCancel} disabled={save.isPending} className="px-2.5 py-1 text-xs">
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * The follow-up list inside the modal. Newest first, because the question is
 * usually "what happened last".
 */
export function FollowUpList({
  activities,
  emptyHint,
}: {
  activities: Activity[];
  emptyHint: string;
}) {
  const timeZone = useAccountTimeZone();

  if (activities.length === 0) {
    return <p className="py-6 text-center text-sm text-slate-400">{emptyHint}</p>;
  }

  return (
    <ol className="relative space-y-5 border-l-2 border-slate-200 pl-8">
      {activities.map((activity) => {
        const Icon = ACTIVITY_KINDS.find((k) => k.value === activity.type)?.icon ?? HistoryIcon;
        return (
          <li key={activity.id} className="relative">
            <span
              className={cx(
                "absolute -left-[2.6rem] flex size-9 items-center justify-center rounded-full ring-4 ring-white",
                activity.type === "note"
                  ? "bg-violet-100 text-violet-700"
                  : "bg-sky-100 text-sky-700",
              )}
            >
              <Icon size={18} />
            </span>
            <p className="text-sm font-medium text-slate-900">
              {activity.subject || activity.type_label}
            </p>
            <p className="text-xs text-slate-400">
              {formatDateTime(activity.created_at, timeZone)}
              {formatElapsed(activity.created_at) ? ` · ${formatElapsed(activity.created_at)}` : ""}
            </p>
            {activity.body ? (
              <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-600">{activity.body}</p>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function HistoryIcon({ size }: { size?: number }) {
  return <span style={{ fontSize: size ?? 12 }}>•</span>;
}